/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SWMForm.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Button, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomFormTextFieldV2 from "../../common/textField/CustomFormTextFieldV2";
import MultiselectConnectorV2 from "../../common/connectors/MultiselectConnectorV2";
import appTheme from "../../../../assets/AppTheme/appTheme";
import { useDispatch } from "react-redux";
import { HandleApiActions, labCar } from "../../../../redux/actions";
import actionType from "../../../../redux/actionTypes";
import typeApprovalAPI from "../../../../redux/actions/typeApprovalAPI";
import ruleEngineActions from "../../../../redux/actions/ruleEngineAction";
import CustomSelectV2 from "../../common/select/CustomSelectV2";

function SWMForm({
  openSWModuleReplaceDialog,
  setOpenSWModuleReplaceDialog,
  handleSWModuleReplace,
  selectedSWModuleReplaceDetails,
  setSelectedSWModuleReplaceDetails,
  vfOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  title,
  supplierOptionsList,
  disableFields = [],
}) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [version, setVersion] = useState("");
  const [supplierName, setSupplierName] = useState(null);
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVfIds, setSelectedVfIds] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false); // State to track confirmation dialog
  const linkModuleType = { SWM: 1, EE: 2 };
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [selectedSWM, setSelectedSWM] = useState(null);

  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
  const [selectedVariantsMany, setSelectedVariantsMany] = useState([]);
  const [githubLink, setGithubLink] = useState("");

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  //Rule set
  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  // Rule set question flow
  const [ruleSetTree, setRuleSetTree] = useState(null);
  // path: [{ node: {type,data}, answer: 'yes'|'no'|null }]
  const [rulePath, setRulePath] = useState([]);

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath.length) return;

    // copy up to index (inclusive) and set the chosen answer
    const newPath = rulePath
      .slice(0, idx + 1)
      .map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      // push the next node and reset its answer
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else {
      // If we have a category, this is a terminal node
      if (currentEntry?.node?.data?.category) {
        // Check if homologation is required for this category
        if (currentEntry?.node?.data?.requires_homologation) {
          // If homologation is required, call replaceChangesAndClose
          replaceChangesAndClose();
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        } else {
          // If homologation is not required, just close the dialog
          setRulePath(newPath);
          setShowRuleSetQuestionDialog(false);
          setRuleSetTree(null);
          setRulePath([]);
          setSelectedRuleSet("");
        }
      } else {
        // Otherwise just update the current answer and keep dialog open
        setRulePath(newPath);
      }
    }
  };
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);

  const dispatch = useDispatch();

  /* -------------- UseEffects -------------- */

  useEffect(() => {
    if (openSWModuleReplaceDialog == true && selectedSWModuleReplaceDetails) {
      setName(selectedSWModuleReplaceDetails?.name || "");
      setVersion(selectedSWModuleReplaceDetails?.version || "");
      setDescription(selectedSWModuleReplaceDetails?.description || "");
      setSupplierName(selectedSWModuleReplaceDetails?.supplier_name || null);
      setSelectedVariantsMany(
        variantOptionList
          ?.filter(
            (variant) =>
              variant?.value !== selectedSingleVariantDetails?.variant_id &&
              selectedSWModuleReplaceDetails?.variant_ids
                ?.map((variant) => variant)
                ?.includes(variant?.value),
          )
          ?.map((data) => parseInt(data?.value)) || [],
      );
      setSummary(selectedSWModuleReplaceDetails?.summary || "");
      setSelectedVfIds(
        (selectedSWModuleReplaceDetails?.vf_ids || [])
          ?.map((vfId) => Number(vfId))
          ?.filter((vfId) => !isNaN(vfId)),
      );
      setDiscp(selectedSWModuleReplaceDetails?.reason || "");
      setGithubLink(
        selectedSWModuleReplaceDetails?.meta_details?.linked_meta_link || "",
      );
    }
  }, [openSWModuleReplaceDialog, selectedSWModuleReplaceDetails]);

  /* -------------- Replace SW Module -------------- */

  const clearReplaceSWModuleFields = () => {
    setName("");
    setNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setSelectedVariantsMany([]);
    setDescriptionError("");
    setSummary("");
    setSummaryError("");
    setSelectedVfIds([]);
    setDiscp("");
    setDiscpError("");
  };

  const isReplaceSWModuleValid = () => {
    return (
      !nameError &&
      !versionError &&
      !descriptionError &&
      selectedVfIds?.length > 0 &&
      (name?.trim() !== selectedSWModuleReplaceDetails?.name ||
        version?.trim() !== selectedSWModuleReplaceDetails?.version ||
        description?.trim() !== selectedSWModuleReplaceDetails?.description ||
        summary?.trim() !== selectedSWModuleReplaceDetails?.summary ||
        selectedVfIds?.length !== selectedSWModuleReplaceDetails?.vf_ids.length)
    );
  };

  const isUpdateDraftSWComponentValid = () => {
    return (
      !nameError &&
      !versionError &&
      !descriptionError &&
      selectedVfIds?.length > 0
    );
  };
  const isUpdateAgainDraftSWComponentValid = () => {
    return !nameError && name;
  };
  const handleCloseSWModuleReplace = () => {
    setOpenSWModuleReplaceDialog(false);

    clearReplaceSWModuleFields();

    setSelectedSWModuleReplaceDetails(null);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
  };

  const handleReplaceClick = () => {
    setShowConfirmation(true); // Show confirmation dialog when update is clicked
  };

  const handleUpdateDraftClickSW = () => {
    const reqObj = {
      supplier_name: supplierName,
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      version: version,
      save_as_draft: false,
      name: name,
      ...(summary && { summary }),
      description: description,
      vf_ids: selectedVfIds,
    };
    dispatch(
      HandleApiActions({
        api_name: "/PCM/swm/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_SW_MODULE_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getSWModulesList();
      handleCloseSWModuleReplace();
    });
  };
  const handleUpdateAgainDraftClickSW = () => {
    const reqObj = {
      ...(supplierName ? { supplier_name: supplierName } : null),
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      ...(version ? { version: version } : null),
      save_as_draft: true,
      name: name,
      ...(summary && { summary }),
      ...(description ? { description: description } : null),
      ...(selectedVfIds?.length > 0 ? { vf_ids: selectedVfIds } : null),
    };
    dispatch(
      HandleApiActions({
        api_name: "/PCM/swm/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_SW_MODULE_DRAFT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getSWModulesList();
      handleCloseSWModuleReplace();
    });
  };

  const confirmReplaceAndRedirect = async ({
    selectedSWModuleReplaceDetails,
    willImptIntrOnly = false,
  }) => {
    if (
      selectedSWModuleReplaceDetails &&
      selectedSWModuleReplaceDetails?.swm_id
    ) {
      const reqObj = {
        ...(supplierName && { supplier_name: supplierName }),
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        variants_ids: selectedEEComponentDetails?.variant_ids,
        version: version,
        name: name,
        ...(summary && { summary }),
        description: description,
        vf_ids: selectedVfIds,
        will_it_impact: true,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        );
        if (!response.error) {
          handleCloseSWModuleReplace();
          willImptIntrOnly
            ? handleActionType2DialogOpen(selectedSWModuleReplaceDetails)
            : handleActionType1Dialogpen(selectedSWModuleReplaceDetails);
          if (willImptIntrOnly) {
            const resp = await dispatch(
              HandleApiActions({
                ...labCar.update_action_type,
                params: {
                  current_time: new Date().toISOString(),
                  project_id: Number(
                    selectedSWModuleReplaceDetails?.project_id,
                  ),
                  variant_id: Number(selectedSingleVariantDetails?.variant_id),
                  ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
                  swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
                  is_dependency_linking: true,
                },
                show_toast: false,
              }),
            );
          }
          getSWModulesList();
        }
      } catch (error) {
      }
    }
  };

  const handleCloseSWModuleReplaceDialog = () => {
    setOpenSWModuleReplaceDialog(false);
  };

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      tickets_type: 1,
    };

    try {
      // First API call
      const response = await dispatch(
        HandleApiActions({
          ...typeApprovalAPI.get_tickets,
          params: reqObj,
          show_toast: false,
        }),
      );

      // Extract and deduplicate standards
      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              .map((ticket) => ticket?.ls_details?.standard_number)
              .filter((std) => Boolean(std)) // remove empty/undefined
            : [],
        ),
      ];

      if (standardsWithRuleSet.length > 0) {
        // Second API call with standards
        const standardWithRuleSet = await dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_engine_with_standards,
            params: { standard: standardsWithRuleSet },
            show_toast: false,
          }),
        );

        if (
          standardWithRuleSet?.data &&
          (Array.isArray(standardWithRuleSet.data)
            ? standardWithRuleSet.data.length > 0
            : Object.keys(standardWithRuleSet.data).length > 0)
        ) {
          // Handle the case where there are standards with rulesets
          handleCloseSWModuleReplaceDialog();
          setShowRuleSetListDialog(true);
          // store the full rule objects so we can send the id later
        } else {
          updateChangesWithoutStandardAndClose();
        }
        return standardWithRuleSet.data || [];
      } else {
        replaceChangesAndClose();
        return [];
      }
    } catch (error) {
      return false;
    }
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseSWModuleReplace();
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_rule_set_tree,
          params: {
            rule_set_id: ruleSetIdToUse,
          },
          show_toast: false,
        }),
      );

      if (response?.data) {
        // Store the full tree and open the question dialog
        setRuleSetTree(response.data);
        // Normalize root node into { type, data }
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setRulePath([{ node: root, answer: null }]);
          setShowRuleSetQuestionDialog(true);
        }
      }
    } catch (error) {
    }
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      version: version,
      will_it_impact: false,
      requires_homologation: true,

      reason: discp,
    };
    try {
      const response = await dispatch(
        HandleApiActions({
          ...labCar.update_sw_module,
          params: reqObj,
          show_toast: true,
        }),
      );
      getSWModulesList();
      if (!response.error) {
        handleCloseSWModuleReplace();
      }
      if (response?.data?.snow_resp) {
        setTicketsDialogOpen(true);
      }
    } catch (error) {
    }
  };

  const replaceChangesAndClose = async () => {
    // Get requires_homologation value from the last node in the rule path
    const lastNode = rulePath[rulePath.length - 1]?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;
    if (
      selectedSWModuleReplaceDetails &&
      selectedSWModuleReplaceDetails?.swm_id
    ) {
      const reqObj = {
        supplier_name: supplierName,
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        variant_ids: selectedEEComponentDetails?.variant_ids,
        name: name,
        ...(summary && { summary }),
        description: description,
        vf_ids: selectedVfIds,
        version: version,
        will_it_impact: false,
        requires_homologation: requiresHomologation,
        reason: discp,
      };
      try {
        const response = await dispatch(
          HandleApiActions({
            ...labCar.replace_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        );
        getSWModulesList();
        if (!response.error) {
          handleCloseSWModuleReplace();
        }
        if (response?.data?.snow_resp) {
          setTicketsDialogOpen(true);
        }
      } catch (error) {
      }
    }
  };

  const handleReplaceSWModule = ({ }) => {
    if (
      selectedSWModuleReplaceDetails &&
      selectedSWModuleReplaceDetails?.swm_id
    ) {
      handleSWModuleReplace({
        supplier_name: supplierName,
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        version: version,
        name: name,
        summary: summary,
        description: description,
        vf_ids: selectedVfIds?.map(Number) || [],
      });

      handleCloseSWModuleReplace();
    }
  };

  const handleActionType1Dialogpen = (selectedSWModuleReplaceDetails) => {
    setSelectedSWM(selectedSWModuleReplaceDetails);
    setActionType1DialogOpen(true);
  };
  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogOpen = (selectedSWModuleReplaceDetails) => {
    setSelectedSWM(selectedSWModuleReplaceDetails);
    setActionType2DialogOpen(true);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };
  return (
    <Stack spacing={2} mb={2}>
      <CustomFormTextFieldV2
        size={"small"}
        label={"Number"}
        placeholder={"Number"}
        type={"text"}
        fldType={"text"}
        value={name}
        setValue={setName}
        valueError={nameError}
        setValueError={setNameError}
        disabled={disableFields.includes("number")}
      />
      <CustomFormTextFieldV2
        size={"small"}
        label={"Version"}
        placeholder={"Version"}
        type={"text"}
        fldType={"textR"}
        value={version}
        setValue={setVersion}
        valueError={versionError}
        setValueError={setVersionError}
        disabled={disableFields.includes("version")}
      />
      <CustomFormTextFieldV2
        size={"small"}
        label={"Description"}
        placeholder={"Description"}
        type={"text"}
        fldType={"text"}
        value={description}
        setValue={setDescription}
        valueError={descriptionError}
        setValueError={setDescriptionError}
        disabled={disableFields.includes("description")}
      />
      <CustomFormTextFieldV2
        size={"small"}
        label={"Summary"}
        placeholder={"Summary"}
        type={"text"}
        value={summary}
        setValue={setSummary}
        disabled={disableFields.includes("summary")}
      />

      <CustomSelectV2
        required={false}
        size={"small"}
        label={"Select Supplier Name"}
        optionsList={supplierOptionsList}
        selectedValue={supplierName}
        handleOnChange={(e) => setSupplierName(e.target.value)}
        disabled={disableFields.includes("supplierName")}
      />
      <CustomFormTextFieldV2
        size="small"
        label="GitHub Link"
        placeholder="https://github.com/username/repo"
        type="text"
        fldType="text"
        value={githubLink}
        setValue={(val) => {
          setGithubLink(val);
        }}
        disabled={true}

      />

      <MultiselectConnectorV2
        size={"small"}
        label={"Select Vehicle Functions"}
        optionsList={
          selectedSingleVariantDetails?.vehicle_function_details?.map((el) => {
            return {
              value: el.id,
              label: `${el.abbreviation} - ${el.name}`,
            };
          }) || []
        }
        selectedValues={selectedVfIds}
        setSelectedValues={setSelectedVfIds}
        disabled={true}
      />


      {showConfirmation && (
        <Stack spacing={2}>
          <Typography variant="body1">
            Will it impact the existing dependencies?
          </Typography>
          <Stack direction="row" spacing={2} justifyContent={"center"}>
            <Button
              sx={{ ...appTheme.commonBtnStyle }}
              onClick={() =>
                confirmReplaceAndRedirect({
                  selectedSWModuleReplaceDetails,
                })
              }
            >
              Yes
            </Button>
            <Button
              sx={{ ...appTheme.commonBtnStyle }}
              onClick={() => {
                setShowConfirmation(false);
                setShowIntersectionImpactPrompt(true);
              }}
            >
              No
            </Button>
          </Stack>
        </Stack>
      )}

      {showIntersectionImpactPrompt && (
        <Stack spacing={2}>
          <Typography variant="body1">
            Will it impact the existing intersection?
          </Typography>
          <Stack direction="row" spacing={2} justifyContent={"center"}>
            <Button
              sx={{ ...appTheme.commonBtnStyle }}
              onClick={() =>
                confirmReplaceAndRedirect({
                  selectedSWModuleReplaceDetails,
                  willImptIntrOnly: true,
                })
              }
            >
              Yes
            </Button>
            <Button
              sx={{ ...appTheme.commonBtnStyle }}
              onClick={checkForStandardWithRuleSet}
            >
              No
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

export default SWMForm;
